/**
 * Length of the forecast window in days, counted from model date + 1.
 * Change this (or pass an explicit horizon) if the pipeline horizon moves off a
 * 7-day week — every window calculation below derives from it.
 */
export const FORECAST_HORIZON_DAYS = 7;

/** Compact YYYYMMDD -> YYYY-MM-DD, or null when not a valid compact date */
function toYmd(compact) {
  const s = String(compact || '').trim();
  if (!/^\d{8}$/.test(s)) return null;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

/** Local calendar YYYY-MM-DD (never toISOString, which shifts the day by timezone) */
function formatLocalYmd(date) {
  if (!date || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Local-midnight Date from YYYY-MM-DD (bare ISO dates parse as UTC otherwise) */
function localDateFromYmd(ymd) {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Row belongs to the latest model-date forecast week in collated data */
export function isLatestModelDateRow(row, latestModelDate) {
  if (!latestModelDate) return false;
  const md = String(row?.model_date || row?.modelDate || '').trim();
  return md === String(latestModelDate).trim();
}

/**
 * Inclusive forecast window for a model date, as local calendar YYYY-MM-DD:
 * model date + 1 through model date + horizon. Returns null on a bad model date.
 */
export function getForecastWindow(latestModelDate, horizonDays = FORECAST_HORIZON_DAYS) {
  const modelYmd = toYmd(latestModelDate);
  const modelDate = localDateFromYmd(modelYmd);
  if (!modelDate) return null;
  const horizon = Number.isFinite(horizonDays) && horizonDays > 0
    ? Math.floor(horizonDays)
    : FORECAST_HORIZON_DAYS;

  const start = new Date(modelDate);
  start.setDate(start.getDate() + 1);
  const end = new Date(modelDate);
  end.setDate(end.getDate() + horizon);
  return { start: formatLocalYmd(start), end: formatLocalYmd(end) };
}

/** Date falls inside model date + 1 .. model date + horizon (calendar dates, YYYY-MM-DD) */
export function isInLatestForecastWeek(dateStr, latestModelDate, horizonDays = FORECAST_HORIZON_DAYS) {
  const window = getForecastWindow(latestModelDate, horizonDays);
  if (!window || !dateStr) return false;
  const d = String(dateStr).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  return d >= window.start && d <= window.end;
}

/**
 * True when a forecast date has not been actualised yet as of the run date.
 * Actuals only exist for days that closed before the run, so anything on or
 * after the run date is still pending.
 */
export function isActualPending(dateStr, runDate) {
  const runYmd = toYmd(runDate);
  if (!runYmd) return false;
  const d = String(dateStr || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  return d >= runYmd;
}

/**
 * True when the whole forecast window closed before the run date, i.e. the
 * week's actuals and accuracy are fully available and must not be hidden.
 */
export function isForecastWeekElapsed(latestModelDate, runDate, horizonDays = FORECAST_HORIZON_DAYS) {
  const window = getForecastWindow(latestModelDate, horizonDays);
  const runYmd = toYmd(runDate);
  if (!window || !runYmd) return false;
  return window.end < runYmd;
}

/**
 * Remove actuals / accuracy from daily collated rows for the latest model week.
 * Only rows still awaiting actuals as of runDate are blanked; once a forecast
 * day has closed its actuals are real and stay visible. Omitting runDate keeps
 * the old behaviour of blanking the entire latest week.
 */
export function stripLatestWeekActuals(row, latestModelDate, runDate) {
  if (!isLatestModelDateRow(row, latestModelDate)) return row;
  if (runDate && !isActualPending(row?.date || row?.Date, runDate)) return row;
  return {
    ...row,
    actual_qty: null,
    APE_component_model: null,
    MAD_component_model: null,
    status_component_model: null,
  };
}

/**
 * Remove actual-based aggregates for the latest model week column. Skipped once
 * the week's forecast window has fully elapsed relative to runDate.
 */
export function stripLatestWeekAggregatedActuals(row, latestModelDate, runDate) {
  if (!isLatestModelDateRow(row, latestModelDate)) return row;
  if (runDate && isForecastWeekElapsed(latestModelDate, runDate)) return row;
  return {
    ...row,
    avg_mape: null,
    avg_mad: null,
    num_days: null,
    avg_daily_units_sold: null,
  };
}
